/**
 * Looks up whether an ID number is already registered as a `taxId` in the
 * onboarding submissions table. Used by the documents step to detect a
 * returning partner from the OCR-extracted owner ID.
 *
 * Failures are swallowed (treated as "not registered") so a transient
 * network problem does not block the onboarding flow.
 */

import { apiClient } from 'shared/lib/apiClient';
import { logger } from 'shared/lib/logger';

export interface CheckTaxIdResponse {
  exists: boolean;
}

export const checkTaxId = async (taxId: string): Promise<CheckTaxIdResponse> => {
  const normalized = String(taxId ?? '').replace(/\D/g, '');
  if (!normalized) return { exists: false };
  try {
    const res = await apiClient.get<CheckTaxIdResponse>('/onboarding/check-tax-id', {
      params: { taxId: normalized },
    });
    return { exists: Boolean(res.data?.exists) };
  } catch (err) {
    logger.warn('[HAAT Partner Connect] taxId duplicate check failed', err);
    return { exists: false };
  }
};

export interface DuplicateCheckResponse {
  email: boolean;
  phone: boolean;
  taxId: boolean;
  exists: boolean;
}

/**
 * Checks whether the owner email, owner phone, or business tax/osek ID are
 * already registered in a prior submission. Fails open (treats a network error
 * as "not registered") so a transient problem does not block onboarding — the
 * backend enforces the same guard authoritatively on submit.
 */
export const checkDuplicate = async (input: {
  email?: string;
  phone?: string;
  taxId?: string;
}): Promise<DuplicateCheckResponse> => {
  const email = String(input.email ?? '').trim();
  const phone = String(input.phone ?? '').replace(/\D/g, '');
  const taxId = String(input.taxId ?? '').replace(/\D/g, '');
  const empty: DuplicateCheckResponse = {
    email: false,
    phone: false,
    taxId: false,
    exists: false,
  };
  if (!email && !phone && !taxId) return empty;
  try {
    const res = await apiClient.get<DuplicateCheckResponse>(
      '/onboarding/check-duplicate',
      { params: { email, phone, taxId } },
    );
    return {
      email: Boolean(res.data?.email),
      phone: Boolean(res.data?.phone),
      taxId: Boolean(res.data?.taxId),
      exists: Boolean(res.data?.exists),
    };
  } catch (err) {
    logger.warn('[HAAT Partner Connect] duplicate check failed', err);
    return empty;
  }
};
